import "@/App.css";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { AuthProvider } from "./context/AuthContext";
import { initTracking } from "./utils/trackingLoader";
import { Sentry } from "./utils/sentry";
import ErrorFallback from "./components/ErrorFallback";
import ChunkLoadErrorBoundary from "./components/ChunkLoadErrorBoundary";
import { captureUTM } from "./utils/utm";
import { trackPageView } from "./utils/analytics";

// Landing page stays EAGER — it's the entry route; keeping it in the main
// bundle avoids a first-paint waterfall for organic visits.
import LandingPage from "./pages/LandingPage";

// Coming Soon teaser — EAGER so maintenance mode renders instantly without a
// lazy-chunk "Loading…" flash.
import ComingSoonPage from "./pages/ComingSoonPage";

// User routes — each in its own lazy chunk. libphonenumber-js (130 KB raw),
// used only by QuestionnaireContainer + ContactUsPage, migrates into these
// chunks automatically via webpack's tree-shaking.
const QuestionnairePage = lazy(() => import(/* webpackChunkName: "route-questionnaire" */ "./pages/QuestionnairePage"));
const GeneratePlanPage = lazy(() => import(/* webpackChunkName: "route-generate" */ "./pages/GeneratePlanPage"));
const PlanViewerPage = lazy(() => import(/* webpackChunkName: "route-plan" */ "./pages/PlanViewerPage"));
const LoginPage = lazy(() => import(/* webpackChunkName: "route-login" */ "./pages/LoginPage"));
const UserDashboard = lazy(() => import(/* webpackChunkName: "route-dashboard" */ "./pages/UserDashboard"));
const ContactUsPage = lazy(() => import(/* webpackChunkName: "route-contact" */ "./pages/ContactUsPage"));
const DynamicPage = lazy(() => import(/* webpackChunkName: "route-cms" */ "./pages/DynamicPage"));
const BlogListPage = lazy(() => import(/* webpackChunkName: "route-blog" */ "./pages/BlogListPage"));
const BlogDetailPage = lazy(() => import(/* webpackChunkName: "route-blog" */ "./pages/BlogDetailPage"));
const PricingPage = lazy(() => import(/* webpackChunkName: "route-pricing" */ "./pages/PricingPage"));
const PaymentSuccessPage = lazy(() => import(/* webpackChunkName: "route-payment" */ "./pages/PaymentSuccessPage"));
const NotFoundPage = lazy(() => import(/* webpackChunkName: "route-404" */ "./pages/NotFoundPage"));

// Admin routes — grouped into a SINGLE shared "admin" chunk. TipTap (~400 KB
// raw) and Recharts+d3 (~316 KB raw) are dragged into this chunk by the
// TipTapEditor + AdminDashboardPage imports, so end users never download
// them.
const AdminLoginPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminLoginPage"));
const AdminAuthCallback = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminAuthCallback"));
const AdminDashboardPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminDashboardPage"));
const AdminOrdersPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminOrdersPage"));
const AdminCouponsPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminCouponsPage"));
const AdminMarketingPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminMarketingPage"));
const AdminPagesPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminPagesPage"));
const AdminContactsPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminContactsPage"));
const AdminBlogsPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminBlogsPage"));
const AdminBlogEditorPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminBlogEditorPage"));
const AdminSettingsPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminSettingsPage"));
const AdminAdminsPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminAdminsPage"));
const AdminDetailPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminDetailPage"));
const AdminUsersPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminUsersPage"));
const AdminUserDetailPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminUserDetailPage"));
const AdminDropoffsPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminDropoffsPage"));
const AdminComingSoonPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/admin/AdminComingSoonPage"));

// Routes that must never be indexed (private app, auth, admin). Rendered via
// React 19 document metadata hoisting.
const NOINDEX_PREFIXES = [
  "/questionnaire", "/generate", "/generate-plan", "/plan", "/dashboard",
  "/login", "/payment", "/payment-success", "/admin",
];

function RobotsMeta() {
  const { pathname } = useLocation();
  const noindex = NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
  const origin = typeof window !== "undefined" ? window.location.origin : "https://dietbanao.com";
  return (
    <>
      <link rel="canonical" href={`${origin}${pathname}`} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
    </>
  );
}

function AnalyticsBridge() {
  const location = useLocation();

  useEffect(() => {
    initTracking(process.env.REACT_APP_BACKEND_URL);
    captureUTM();
  }, []);

  useEffect(() => {
    captureUTM();
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
}

// Lightweight full-viewport fallback rendered while a lazy route chunk loads.
// Intentionally minimal (no CSS-in-JS, no imports) so it never blocks first
// paint on the eager landing route.
function RouteFallback() {
  return (
    <div
      data-testid="route-lazy-fallback"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#888',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      Loading…
    </div>
  );
}

// Extracted so React doesn't create a new component type on every App render.
function SentryFallback({ resetError }) {
  return <ErrorFallback resetError={resetError} />;
}

// Maintenance / Coming Soon gate. When maintenance_mode is ON (from
// /api/config/public), public visitors see the ComingSoonPage. Logged-in
// admins (db_admin_token present) and all /admin routes always see the real
// site so the admin panel stays reachable.
function MaintenanceGate({ children }) {
  const { pathname } = useLocation();
  const [state, setState] = useState("loading"); // loading | on | off

  useEffect(() => {
    let active = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/public`)
      .then((r) => r.json())
      .then((d) => { if (active) setState(d && d.maintenance_mode ? "on" : "off"); })
      .catch(() => { if (active) setState("off"); });
    return () => { active = false; };
  }, []);

  const isAdminRoute = pathname.startsWith("/admin");
  const isComingSoonRoute = pathname === "/coming-soon";
  const adminToken = typeof window !== "undefined" && localStorage.getItem("db_admin_token");

  // Admin panel + login must always load, maintenance or not.
  if (isAdminRoute) return children;
  // Admins preview the live site; /coming-soon is always directly viewable.
  if (adminToken || isComingSoonRoute) return children;
  // Avoid flashing the full site before the flag resolves.
  if (state === "loading") return <RouteFallback />;
  if (state === "on") return <ComingSoonPage />;
  return children;
}

function App() {
  return (
    <div className="App">
      <Sentry.ErrorBoundary fallback={SentryFallback}>
        <ChunkLoadErrorBoundary>
        <GoogleReCaptchaProvider reCaptchaKey="6LcikX4sAAAAAEftLWg9XoXo_Gz7nuSPq31b-StZ">
          <AuthProvider>
          <BrowserRouter>
            <AnalyticsBridge />
            <RobotsMeta />
            <MaintenanceGate>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/coming-soon" element={<ComingSoonPage />} />
            <Route path="/questionnaire" element={<QuestionnairePage />} />
            <Route path="/generate/:questionnaireId" element={<GeneratePlanPage />} />
            <Route path="/generate" element={<GeneratePlanPage />} />
            {/* QA-M4 aliases: friendlier fallbacks for hyphenated URL variants */}
            <Route path="/generate-plan/:questionnaireId" element={<GeneratePlanPage />} />
            <Route path="/generate-plan" element={<GeneratePlanPage />} />
            <Route path="/plan/:planId" element={<PlanViewerPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={<UserDashboard />} />
            <Route path="/contact" element={<ContactUsPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            {/* QA-M4 alias: hyphenated variant for payment-return links */}
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/admin/orders" element={<AdminOrdersPage />} />
            <Route path="/admin/coupons" element={<AdminCouponsPage />} />
            {/* Dynamic CMS pages */}
            <Route path="/privacy-policy" element={<DynamicPage />} />
            <Route path="/terms-and-conditions" element={<DynamicPage />} />
            <Route path="/refund-policy" element={<DynamicPage />} />
            <Route path="/about-us" element={<DynamicPage />} />
            {/* Admin */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/auth/callback" element={<AdminAuthCallback />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/users/:userId" element={<AdminUserDetailPage />} />
            <Route path="/admin/dropoffs" element={<AdminDropoffsPage />} />
            <Route path="/admin/admins" element={<AdminAdminsPage />} />
            <Route path="/admin/admins/:adminId" element={<AdminDetailPage />} />
            <Route path="/admin/pages" element={<AdminPagesPage />} />
            <Route path="/admin/contacts" element={<AdminContactsPage />} />
            <Route path="/admin/blogs" element={<AdminBlogsPage />} />
            <Route path="/admin/blogs/:blogId" element={<AdminBlogEditorPage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
            <Route path="/admin/marketing" element={<AdminMarketingPage />} />
            <Route path="/admin/coming-soon" element={<AdminComingSoonPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          </MaintenanceGate>
        </BrowserRouter>
      </AuthProvider>
      </GoogleReCaptchaProvider>
      </ChunkLoadErrorBoundary>
      </Sentry.ErrorBoundary>
    </div>
  );
}

export default App;
